import '../css/LobbyBrowser.scss';
import { Key } from '../../libs/Key';
import { browsableLobbies, isFullLobby, LOBBY_STATUS, lobbyRoomName, MAX_PASSWORD_LENGTH, MAX_ROOM_NAME_LENGTH, MIN_LOBBY_PLAYERS, randomRoomName } from '../multiplayer/lobbyPolicy';
import { createLobby, currentRoomName, isMultiplayerConnected, isUpdateAvailable, joinLobby, leaveMultiplayer, listLobbies, localClient, multiplayerState, quickJoinLobby, startMultiplayer, updateLobbySettings } from '../multiplayer/multiplayerManager';
import { game } from '../Game';
import { Settings } from '../Settings';
import { localize } from '../utils/Localization';

export const LOCK_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 7V5a4 4 0 0 1 8 0v2h1v8H3V7h1zm2 0h4V5a2 2 0 0 0-4 0v2z"/></svg>';

const customGUIContainer = document.getElementById('game-ui-container');

let browser = null;
let refreshTimer = null;
let refreshing = null;
let lobbies = [];
let listError = false;
let loaded = false;
const entryElements = new Map();

let dialog = null;

const element = (tag, className, text) => {
	const node = document.createElement(tag);
	if (className) node.className = className;
	if (text !== undefined) node.innerText = text;
	return node;
};

// Typing in a field must not reach the game's key handlers.
const textField = (className, {maxLength, placeholder, label, onEnter}) => {
	const input = element('input', className);
	input.type = 'text';
	input.maxLength = maxLength;
	input.autocomplete = 'off';
	input.spellcheck = false;
	input.placeholder = placeholder || '';
	input.setAttribute('aria-label', label);
	input.addEventListener('keydown', event => {
		event.stopPropagation();
		if (event.key === 'Enter' && !event.isComposing) {
			event.preventDefault();
			onEnter?.();
		}
	});
	input.addEventListener('focus', () => Key.reset());
	return input;
};

export const closeMultiplayerDialog = () => {
	dialog?.remove();
	dialog = null;
};

const openDialog = ({title, body, buttons, className = ''}) => {
	closeMultiplayerDialog();
	dialog = element('div', 'mp-dialog-backdrop');
	const panel = element('div', `mp-dialog ${className}`);
	panel.setAttribute('role', 'dialog');
	panel.setAttribute('aria-modal', 'true');
	if (title) panel.appendChild(element('div', 'dialog-title', title));
	const content = element('div', 'dialog-body');
	content.append(...body);
	panel.appendChild(content);
	const buttonRow = element('div', 'dialog-buttons');
	buttons.forEach(({label, className: buttonClass, onClick}) => {
		const button = element('div', `dialog-button ${buttonClass || ''}`, label);
		button.setAttribute('role', 'button');
		button.onclick = onClick;
		buttonRow.appendChild(button);
	});
	panel.appendChild(buttonRow);
	dialog.appendChild(panel);
	customGUIContainer.appendChild(dialog);
	return panel;
};

export const showMultiplayerNotice = (key, values) => {
	openDialog({
		className: 'notice',
		body: [element('p', 'dialog-text', localize(key, values))],
		buttons: [{label: localize('multiplayer_ok'), className: 'confirm', onClick: closeMultiplayerDialog}],
	});
};

// Poki serves each build from its own URL, so only a refresh of the Poki page updates the game there.
const reloadGame = code => {
	const url = new URL(window.location.pathname, window.location.origin);
	if (code) url.searchParams.set('lobbyID', code);
	window.location.href = url.href;
};

export const showUpdateNotice = (key, code) => {
	openDialog({
		className: 'notice',
		body: [element('p', 'dialog-text', localize(key))],
		buttons: Settings.onPoki
			? [{label: localize('multiplayer_ok'), className: 'confirm', onClick: closeMultiplayerDialog}]
			: [{label: localize('controls_cancel'), className: 'cancel', onClick: closeMultiplayerDialog},
				{label: localize('multiplayer_reload'), className: 'confirm', onClick: () => reloadGame(code)}],
	});
};

export const promptLobbyPassword = (code, wrongPassword = false) => {
	const submit = () => {
		if (!input.value.trim()) return input.focus();
		closeMultiplayerDialog();
		joinLobby(code, input.value);
	};
	const input = textField('dialog-input', {maxLength: MAX_PASSWORD_LENGTH, label: localize('multiplayer_password'), onEnter: submit});
	const error = element('p', 'dialog-error', localize('multiplayer_wrongpassword'));
	error.hidden = !wrongPassword;
	openDialog({
		title: localize('multiplayer_passwordrequired'),
		body: [element('p', 'dialog-text', localize('multiplayer_enterpassword')), input, error],
		buttons: [
			{label: localize('controls_cancel'), className: 'cancel', onClick: closeMultiplayerDialog},
			{label: localize('multiplayer_join'), className: 'confirm', onClick: submit},
		],
	});
	input.focus();
};

export const showLobbySettingsForm = (edit = false) => {
	const current = edit
		? {...multiplayerState.lobbySettings, name: currentRoomName(), password: multiplayerState.password}
		: {public: true, name: randomRoomName(), password: '', maxPlayers: Settings.maxMultiplayerPlayers};
	// A new host after a handover does not know the password; an empty field keeps it.
	const passwordKnown = typeof current.password === 'string';
	const minPlayers = edit ? Math.max(MIN_LOBBY_PLAYERS, multiplayerState.peersConnected + 1) : MIN_LOBBY_PLAYERS;
	const values = {public: current.public, maxPlayers: Math.max(minPlayers, current.maxPlayers)};

	const visibility = element('div', 'setting visibility');
	visibility.appendChild(element('div', 'setting-label', localize('multiplayer_visibility')));
	const options = element('div', 'visibility-options');
	const description = element('div', 'setting-description');
	const optionButtons = [true, false].map(isPublic => {
		const option = element('div', 'visibility-option', localize(isPublic ? 'multiplayer_public' : 'multiplayer_private'));
		option.setAttribute('role', 'radio');
		option.onclick = () => {
			values.public = isPublic;
			renderVisibility();
		};
		options.appendChild(option);
		return {option, isPublic};
	});
	const renderVisibility = () => {
		optionButtons.forEach(({option, isPublic}) => {
			option.classList.toggle('selected', values.public === isPublic);
			option.setAttribute('aria-checked', String(values.public === isPublic));
		});
		description.innerText = localize(values.public ? 'multiplayer_public_info' : 'multiplayer_private_info');
	};
	visibility.append(options, description);

	const submit = async () => {
		const settings = {...values, name: name.value || randomRoomName()};
		if (passwordKnown ? password.value !== current.password : password.value.trim()) settings.password = password.value;
		if (!edit) {
			closeMultiplayerDialog();
			createLobby(settings);
			return;
		}
		saveButton.classList.add('busy');
		const saved = await updateLobbySettings(settings);
		if (form.isConnected) {
			if (saved) closeMultiplayerDialog();
			else {
				saveButton.classList.remove('busy');
				error.hidden = false;
			}
		}
	};

	const nameSetting = element('div', 'setting room-name');
	nameSetting.appendChild(element('div', 'setting-label', localize('multiplayer_roomname')));
	const nameRow = element('div', 'name-row');
	const name = textField('dialog-input', {maxLength: MAX_ROOM_NAME_LENGTH, label: localize('multiplayer_roomname'), onEnter: submit});
	name.value = current.name;
	// Poki players only use generated names; see lobbyRoomName.
	name.readOnly = Settings.onPoki;
	const shuffle = element('div', 'shuffle-name');
	shuffle.setAttribute('role', 'button');
	shuffle.setAttribute('aria-label', localize('multiplayer_randomname'));
	shuffle.onclick = () => { name.value = randomRoomName(); };
	nameRow.append(name, shuffle);
	nameSetting.appendChild(nameRow);

	const passwordSetting = element('div', 'setting password');
	passwordSetting.appendChild(element('div', 'setting-label', localize('multiplayer_password')));
	const passwordHint = !passwordKnown && current.hasPassword ? 'multiplayer_keeppassword' : 'multiplayer_optional';
	const password = textField('dialog-input', {maxLength: MAX_PASSWORD_LENGTH, placeholder: localize(passwordHint), label: localize('multiplayer_password'), onEnter: submit});
	password.value = passwordKnown ? current.password : '';
	passwordSetting.appendChild(password);

	const players = element('div', 'setting players');
	players.appendChild(element('div', 'setting-label', localize('multiplayer_maxplayers')));
	const stepper = element('div', 'stepper');
	const less = element('div', 'stepper-button less', '−');
	const count = element('div', 'stepper-value');
	const more = element('div', 'stepper-button more', '+');
	less.setAttribute('aria-label', localize('multiplayer_fewerplayers'));
	more.setAttribute('aria-label', localize('multiplayer_moreplayers'));
	const renderPlayers = () => {
		count.innerText = values.maxPlayers;
		less.setAttribute('aria-disabled', String(values.maxPlayers <= minPlayers));
		more.setAttribute('aria-disabled', String(values.maxPlayers >= Settings.maxMultiplayerPlayers));
	};
	less.onclick = () => { values.maxPlayers = Math.max(minPlayers, values.maxPlayers - 1); renderPlayers(); };
	more.onclick = () => { values.maxPlayers = Math.min(Settings.maxMultiplayerPlayers, values.maxPlayers + 1); renderPlayers(); };
	stepper.append(less, count, more);
	players.appendChild(stepper);

	const error = element('p', 'dialog-error', localize('multiplayer_error_settings'));
	error.hidden = true;

	const form = openDialog({
		className: 'lobby-settings',
		title: localize(edit ? 'multiplayer_lobbysettings' : 'mainmenu_creategame'),
		body: [nameSetting, visibility, passwordSetting, players, error],
		buttons: [
			{label: localize('levelbanner_back'), className: 'cancel', onClick: closeMultiplayerDialog},
			{label: localize(edit ? 'multiplayer_save' : 'multiplayer_create'), className: 'confirm', onClick: submit},
		],
	});
	const saveButton = form.querySelector('.dialog-button.confirm');
	renderVisibility();
	renderPlayers();
};

const selectLobby = lobby => {
	if (!lobby || isFullLobby(lobby)) return;
	if (lobby.hasPassword) promptLobbyPassword(lobby.code);
	else joinLobby(lobby.code);
};

const createEntry = () => {
	const entry = element('div', 'lobby-entry');
	entry.setAttribute('role', 'button');
	entry.innerHTML = /*html*/`
		<div class="lobby-lock">${LOCK_ICON}</div>
		<div class="lobby-text">
			<div class="lobby-name"></div>
			<div class="lobby-level"></div>
		</div>
		<div class="lobby-status"></div>
		<div class="lobby-players"></div>
		<div class="lobby-ping" aria-hidden="true"><i></i><i></i><i></i></div>
	`;
	entry.onclick = () => selectLobby(entry.lobby);
	return entry;
};

const fillEntry = (entry, lobby) => {
	const data = lobby.customData || {};
	const full = isFullLobby(lobby);
	const playing = data.status === LOBBY_STATUS.PLAYING;
	const name = lobbyRoomName(data, lobby.code, Settings.onPoki);
	const level = `${data.level?.title || localize('multiplayer_pickinglevel')} · ${localize('multiplayer_hostedby', {name: data.host || '?'})}`;
	const status = localize(full ? 'multiplayer_full' : playing ? 'multiplayer_status_playing' : 'multiplayer_status_lobby');
	const players = `${lobby.playerCount}/${lobby.maxPlayers || Settings.maxMultiplayerPlayers}`;
	entry.lobby = lobby;
	entry.querySelector('.lobby-name').innerText = name;
	entry.querySelector('.lobby-level').innerText = level;
	entry.querySelector('.lobby-status').innerText = status;
	entry.querySelector('.lobby-players').innerText = players;
	const latency = Number.isFinite(lobby.latency) ? lobby.latency : null;
	const ping = entry.querySelector('.lobby-ping');
	ping.hidden = latency === null;
	ping.dataset.quality = latency === null ? '' : latency < 120 ? 'good' : latency < 250 ? 'ok' : 'bad';
	entry.classList.toggle('locked', lobby.hasPassword);
	entry.classList.toggle('full', full);
	entry.classList.toggle('playing', playing);
	entry.setAttribute('aria-disabled', String(full));
	entry.setAttribute('aria-label', [name, level, status, players, lobby.hasPassword ? localize('multiplayer_password') : ''].filter(Boolean).join(', '));
};

const renderLobbies = () => {
	if (!browser) return;
	const entries = browser.querySelector('.lobby-entries');
	const seen = new Set();
	const elements = lobbies.map(lobby => {
		let entry = entryElements.get(lobby.code);
		if (!entry) {
			entry = createEntry();
			entryElements.set(lobby.code, entry);
		}
		fillEntry(entry, lobby);
		seen.add(lobby.code);
		return entry;
	});
	for (const code of [...entryElements.keys()]) if (!seen.has(code)) entryElements.delete(code);
	const children = [...entries.children];
	if (children.length !== elements.length || children.some((child, index) => child !== elements[index])) {
		// Reordering moves nodes, which drops keyboard focus from a kept entry.
		const focused = elements.includes(document.activeElement) ? document.activeElement : null;
		entries.replaceChildren(...elements);
		focused?.focus({preventScroll: true});
	}

	browser.querySelector('.update-banner').hidden = !isUpdateAvailable();
	const status = browser.querySelector('.list-status');
	const connected = isMultiplayerConnected();
	status.innerText = listError || (loaded && !connected) ? localize('multiplayer_error_connection')
		: !loaded ? localize('multiplayer_searching')
		: lobbies.length ? '' : localize('multiplayer_nolobbies');
	status.classList.toggle('error', listError);
	status.hidden = !status.innerText;
	browser.querySelector('.refresh-button').classList.toggle('busy', !!refreshing);
};

const refreshLobbies = () => {
	if (refreshing) return refreshing;
	refreshing = (async () => {
		try {
			lobbies = browsableLobbies(await listLobbies(), localClient());
			listError = false;
		} catch (error) {
			console.warn('Could not list multiplayer lobbies', error);
			listError = true;
		}
		loaded = true;
		refreshing = null;
		renderLobbies();
	})();
	renderLobbies();
	return refreshing;
};

export const updateLobbyBrowser = () => renderLobbies();

export const generateLobbyBrowser = () => {
	if (browser) return browser;
	browser = element('div', 'lobby-browser');
	browser.innerHTML = /*html*/`
		<div class="title">${localize('mainmenu_multiplayer')}</div>
		<div class="browser-actions">
			<div class="quick-play-button">${localize('mainmenu_quickplay')}</div>
			<div class="create-lobby-button">${localize('mainmenu_creategame')}</div>
		</div>
		<div class="join-code">
			<div class="join-code-label">${localize('multiplayer_havecode')}</div>
			<div class="join-code-row">
				<div class="join-code-button">${localize('multiplayer_join')}</div>
			</div>
		</div>
		<div class="update-banner" hidden>
			<div class="update-text">${localize('multiplayer_update_available')}</div>
			<div class="update-reload">${localize('multiplayer_reload')}</div>
		</div>
		<div class="lobby-list">
			<div class="list-header">
				<div class="list-title">${localize('multiplayer_lobbies')}</div>
				<div class="refresh-button">${localize('multiplayer_refresh')}</div>
			</div>
			<div class="lobby-entries"></div>
			<div class="list-status"></div>
		</div>
		<div class="nav-buttons">
			<div class="back-button">${localize('levelbanner_back')}</div>
		</div>
	`;

	const joinWithCode = () => {
		if (codeInput.value.trim()) joinLobby(codeInput.value);
		else codeInput.focus();
	};
	const codeInput = textField('code-input', {maxLength: 20, placeholder: localize('multiplayer_lobbycode'), label: localize('multiplayer_lobbycode'), onEnter: joinWithCode});
	codeInput.autocapitalize = 'characters';
	browser.querySelector('.join-code-row').prepend(codeInput);
	browser.querySelector('.join-code-button').onclick = joinWithCode;

	const reloadButton = browser.querySelector('.update-reload');
	reloadButton.hidden = Settings.onPoki;
	reloadButton.onclick = () => reloadGame();

	browser.querySelector('.quick-play-button').onclick = () => quickJoinLobby();
	browser.querySelector('.create-lobby-button').onclick = () => showLobbySettingsForm(false);
	browser.querySelector('.refresh-button').onclick = () => refreshLobbies();
	browser.querySelector('.back-button').onclick = () => {
		leaveMultiplayer();
		game.ui.setMainMenuActive('main');
	};

	renderLobbies();
	return browser;
};

export const showLobbyBrowser = () => {
	startMultiplayer().catch(() => {});
	refreshLobbies();
	clearInterval(refreshTimer);
	refreshTimer = setInterval(() => {
		if (!document.hidden && !dialog) refreshLobbies();
	}, Settings.lobbyRefreshInterval);
};

export const hideLobbyBrowser = () => {
	clearInterval(refreshTimer);
	refreshTimer = null;
};
